import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import Lottie from './Lottie'
import { newsService, socialService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useCartsQuery } from '../hooks/useCartsQuery'
import './HomePage.css'

function HomePage({ onNavigate }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [recentAnimation, setRecentAnimation] = useState(null)
  const [notifAnimation, setNotifAnimation] = useState(null)
  const [newsAnimation, setNewsAnimation] = useState(null)
  const [cadisplusAnimation, setCadisplusAnimation] = useState(null)
  const [analyticsAnimation, setAnalyticsAnimation] = useState(null)
  const [cadisAnimation, setCadisAnimation] = useState(null)
  const [showNewsForm, setShowNewsForm] = useState(false)
  const [newsTitle, setNewsTitle] = useState('')
  const [newsContent, setNewsContent] = useState('')
  
  // Vérifier si l'utilisateur est admin
  const isAdmin = Boolean(user?.is_admin)

  // Charger les animations Lottie
  useEffect(() => {
    fetch('/Recent.json').then(res => res.json()).then(setRecentAnimation).catch(console.error)
    fetch('/Notif.json').then(res => res.json()).then(setNotifAnimation).catch(console.error)
    fetch('/News.json').then(res => res.json()).then(setNewsAnimation).catch(console.error)
    fetch('/Cadisplus.json').then(res => res.json()).then(setCadisplusAnimation).catch(console.error)
    fetch('/Analytics_animation.json').then(res => res.json()).then(setAnalyticsAnimation).catch(console.error)
    fetch('/Cadis_animation.json').then(res => res.json()).then(setCadisAnimation).catch(console.error)
  }, [])

  const { data: cartsData, isLoading } = useCartsQuery()
  const carts = cartsData ?? []

  // Récupérer les conversations pour les notifications de messages
  const { data: conversationsData } = useQuery({
    queryKey: ['social-conversations'],
    queryFn: () => socialService.getConversations(),
    retry: false,
    refetchInterval: 30000 // Rafraîchir toutes les 30 secondes
  })

  // Calculer les statistiques
  const stats = useMemo(() => {
    if (!carts.length) {
      return {
        totalDepenses: 0,
        nombrePaniers: 0,
        moyenneParPanier: 0,
        utilisationBudget: 0,
        totalBudget: 0
      }
    }

    const totalDepenses = carts.reduce((sum, cart) => sum + (cart.total || 0), 0)
    const totalBudget = carts.reduce((sum, cart) => sum + (cart.budget || 0), 0)
    const nombrePaniers = carts.length
    const moyenneParPanier = nombrePaniers > 0 ? totalDepenses / nombrePaniers : 0
    const utilisationBudget = totalBudget > 0 ? (totalDepenses / totalBudget) * 100 : 0

    return {
      totalDepenses,
      nombrePaniers,
      moyenneParPanier,
      utilisationBudget,
      totalBudget
    }
  }, [carts])

  // Derniers paniers (triés par date de modification)
  const derniersPaniers = useMemo(() => {
    return [...carts]
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0)
        const dateB = new Date(b.updated_at || b.created_at || 0)
        return dateB - dateA
      })
      .slice(0, 5)
  }, [carts])

  // Notifications récentes (dépassement budget, nouveaux messages, etc.)
  const notifications = useMemo(() => {
    const notifs = []
    const conversations = conversationsData?.conversations || []
    
    // Notifications de dépassement de budget
    carts.forEach(cart => {
      const total = cart.total || 0
      const budget = cart.budget || 0
      if (budget > 0 && total > budget) {
        notifs.push({
          id: `budget-${cart.id}`,
          type: 'budget',
          title: 'Budget dépassé',
          message: `Le panier "${cart.name}" a dépassé son budget`,
          date: new Date(cart.updated_at || cart.created_at),
          cartId: cart.id
        })
      }
    })
    
    // Notifications de nouveaux messages non lus
    conversations.forEach(conv => {
      if (conv.unread_count > 0 && conv.last_message) {
        const senderName = conv.user.display_name || conv.user.username || conv.user.email || 'Quelqu\'un'
        const messagePreview = conv.last_message.content?.substring(0, 50) || 'Nouveau message'
        notifs.push({
          id: `message-${conv.user.id}`,
          type: 'message',
          title: 'Nouveau message',
          message: `${senderName}: ${messagePreview}${conv.last_message.content?.length > 50 ? '...' : ''}`,
          date: new Date(conv.last_message.created_at || conv.last_message.updated_at),
          userId: conv.user.id,
          unreadCount: conv.unread_count
        })
      }
    })
    
    // Trier par date (plus récentes en premier)
    return notifs.sort((a, b) => b.date - a.date).slice(0, 5)
  }, [carts, conversationsData])

  // Récupérer les news depuis l'API
  const { data: newsData, isLoading: isLoadingNews } = useQuery({
    queryKey: ['news'],
    queryFn: () => newsService.getAll(),
    retry: false,
    refetchInterval: 60000 // Rafraîchir toutes les minutes
  })

  const news = newsData?.news || []

  // Mutation pour créer une news
  const createNewsMutation = useMutation({
    mutationFn: ({ title, content }) => newsService.create(title, content),
    onSuccess: () => {
      queryClient.invalidateQueries(['news'])
      setShowNewsForm(false)
      setNewsTitle('')
      setNewsContent('')
    }
  })

  // Mutation pour supprimer une news
  const deleteNewsMutation = useMutation({
    mutationFn: (newsId) => newsService.delete(newsId),
    onSuccess: () => {
      queryClient.invalidateQueries(['news'])
    }
  })

  const handleCreateNews = (e) => {
    e.preventDefault()
    if (newsTitle.trim() && newsContent.trim()) {
      createNewsMutation.mutate({ title: newsTitle.trim(), content: newsContent.trim() })
    }
  }

  const handleCreateCart = () => {
    if (onNavigate) {
      onNavigate('carts')
      // Déclencher la création d'un nouveau panier après un court délai
      setTimeout(() => {
        // Utiliser un événement personnalisé pour déclencher l'ouverture du modal
        const event = new CustomEvent('openCreateCartModal')
        window.dispatchEvent(event)
      }, 500)
    } else {
      navigate('/carts')
    }
  }

  const handleViewAnalytics = () => {
    if (onNavigate) {
      onNavigate('analytics')
    } else {
      navigate('/carts')
    }
  }

  const handleViewCart = (cartId) => {
    navigate(`/carts/${cartId}`)
  }

  if (isLoading) {
    return (
      <div className="home-page">
        <div className="loading">Chargement...</div>
      </div>
    )
  }

  return (
    <div className="home-page">
      <div className="home-header">
        <img 
          src="/Logo_Camit_Blanc_Horizontal.png" 
          alt="Cam'it Logo" 
          className="camit-logo"
        />
        <h2>Tableau de bord</h2>
      </div>

      {/* Widget Statistiques rapides */}
      <div className="home-widgets-grid">
        <div className="home-widget stat-widget">
          <div className="widget-header">
            <h3>Nombre de paniers</h3>
          </div>
          <div className="widget-content">
            <div className="stat-value">{stats.nombrePaniers}</div>
            <div className="stat-label">Paniers créés</div>
          </div>
        </div>

        <div className="home-widget stat-widget">
          <div className="widget-header">
            <h3>Moyenne par panier</h3>
          </div>
          <div className="widget-content">
            <div className="stat-value">€ {stats.moyenneParPanier.toFixed(2)}</div>
            <div className="stat-label">Dépense moyenne</div>
          </div>
        </div>
      </div>

      {/* Widget Derniers paniers et Notifications */}
      <div className="home-widgets-row">
        <div className="home-widget recent-carts-widget">
          <div className="widget-header">
            <div className="widget-header-with-icon">
              {recentAnimation && (
                <div className="widget-icon">
                  <Lottie animationData={recentAnimation} loop={true} autoplay={true} style={{ width: 32, height: 32 }} />
                </div>
              )}
              <h3>Derniers paniers</h3>
            </div>
          </div>
          <div className="widget-content">
            {derniersPaniers.length > 0 ? (
              <div className="recent-carts-list">
                {derniersPaniers.map(cart => (
                  <div 
                    key={cart.id} 
                    className="recent-cart-item"
                    onClick={() => handleViewCart(cart.id)}
                  >
                    <div className="recent-cart-name">{cart.name}</div>
                    <div className="recent-cart-info">
                      <span className="recent-cart-total">€ {cart.total?.toFixed(2) || '0.00'}</span>
                      <span className="recent-cart-date">
                        {new Date(cart.updated_at || cart.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">Aucun panier pour le moment</div>
            )}
          </div>
        </div>

        <div className="home-widget notifications-widget">
          <div className="widget-header">
            <div className="widget-header-with-icon">
              {notifAnimation && (
                <div className="widget-icon">
                  <Lottie animationData={notifAnimation} loop={true} autoplay={true} style={{ width: 32, height: 32 }} />
                </div>
              )}
              <h3>Notifications récentes</h3>
            </div>
          </div>
          <div className="widget-content">
            {notifications.length > 0 ? (
              <div className="notifications-list">
                {notifications.map(notif => (
                  <div 
                    key={notif.id} 
                    className={`notification-item ${notif.type === 'message' ? 'notification-message' : ''}`}
                    onClick={() => {
                      if (notif.cartId) {
                        handleViewCart(notif.cartId)
                      } else if (notif.userId) {
                        // Naviguer vers les messages et ouvrir la conversation
                        // Stocker l'ID de la conversation à sélectionner dans sessionStorage
                        // pour qu'il soit disponible même si SocialFeed n'est pas encore monté
                        sessionStorage.setItem('pendingConversationId', notif.userId)
                        
                        if (onNavigate) {
                          // Changer vers l'application sociale
                          onNavigate('social')
                          // Déclencher un événement pour sélectionner la conversation après un délai
                          // pour laisser le temps à SocialFeed de se monter et d'écouter l'événement
                          setTimeout(() => {
                            const event = new CustomEvent('selectConversation', { detail: { userId: notif.userId } })
                            window.dispatchEvent(event)
                          }, 500)
                        } else {
                          // Fallback: utiliser navigate si onNavigate n'est pas disponible
                          navigate('/carts')
                          setTimeout(() => {
                            const event = new CustomEvent('selectConversation', { detail: { userId: notif.userId } })
                            window.dispatchEvent(event)
                          }, 500)
                        }
                      }
                    }}
                  >
                    <div className="notification-title">
                      {notif.title}
                      {notif.unreadCount > 1 && (
                        <span className="notification-count"> ({notif.unreadCount})</span>
                      )}
                    </div>
                    <div className="notification-message-text">{notif.message}</div>
                    <div className="notification-date">
                      {notif.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">Aucune notification</div>
            )}
          </div>
        </div>
      </div>

      {/* Widget News Cam'it */}
      <div className="home-widgets-row">
        <div className="home-widget news-widget">
          <div className="widget-header">
            <div className="widget-header-with-icon">
              {newsAnimation && (
                <div className="widget-icon">
                  <Lottie animationData={newsAnimation} loop={true} autoplay={true} style={{ width: 32, height: 32 }} />
                </div>
              )}
              <h3>News Cam'it</h3>
            </div>
            {isAdmin && (
              <button 
                className="news-add-btn"
                onClick={() => setShowNewsForm(!showNewsForm)}
                title="Créer une news"
              >
                {showNewsForm ? '✕' : '+'}
              </button>
            )}
          </div>
          <div className="widget-content">
            {isAdmin && showNewsForm && (
              <form className="news-form" onSubmit={handleCreateNews}>
                <input
                  type="text"
                  placeholder="Titre de la news"
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                  className="news-input"
                  maxLength={255}
                  required
                />
                <textarea
                  placeholder="Contenu de la news"
                  value={newsContent}
                  onChange={(e) => setNewsContent(e.target.value)}
                  className="news-textarea"
                  rows={4}
                  maxLength={5000}
                  required
                />
                <div className="news-form-actions">
                  <button type="submit" className="news-submit-btn" disabled={createNewsMutation.isLoading}>
                    {createNewsMutation.isLoading ? 'Publication...' : 'Publier'}
                  </button>
                  <button 
                    type="button" 
                    className="news-cancel-btn"
                    onClick={() => {
                      setShowNewsForm(false)
                      setNewsTitle('')
                      setNewsContent('')
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            )}
            {isLoadingNews ? (
              <div className="empty-state">Chargement...</div>
            ) : news.length > 0 ? (
              <div className="news-list">
                {news.map(item => (
                  <div key={item.id} className="news-item">
                    <div className="news-item-header">
                      <div className="news-title">{item.title}</div>
                      {isAdmin && (
                        <button
                          className="news-delete-btn"
                          onClick={() => {
                            if (window.confirm('Êtes-vous sûr de vouloir supprimer cette news ?')) {
                              deleteNewsMutation.mutate(item.id)
                            }
                          }}
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="news-content">{item.content}</div>
                    <div className="news-date">
                      {new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">Aucune actualité pour le moment</div>
            )}
          </div>
        </div>
      </div>

      {/* Widget Raccourcis et Alertes */}
      <div className="home-widgets-row">
        <div className="home-widget shortcuts-widget">
          <div className="widget-header">
            <h3>Raccourcis rapides</h3>
          </div>
          <div className="widget-content">
            <div className="shortcuts-grid">
              <button className="shortcut-btn" onClick={handleCreateCart}>
                {cadisplusAnimation ? (
                  <div className="shortcut-icon">
                    <Lottie animationData={cadisplusAnimation} loop={true} autoplay={true} style={{ width: 48, height: 48 }} />
                  </div>
                ) : (
                  <span className="shortcut-icon">➕</span>
                )}
                <span className="shortcut-label">Nouveau panier</span>
              </button>
              <button className="shortcut-btn" onClick={handleViewAnalytics}>
                {analyticsAnimation ? (
                  <div className="shortcut-icon">
                    <Lottie animationData={analyticsAnimation} loop={true} autoplay={true} style={{ width: 48, height: 48 }} />
                  </div>
                ) : (
                  <span className="shortcut-icon">📊</span>
                )}
                <span className="shortcut-label">Statistiques</span>
              </button>
              <button className="shortcut-btn" onClick={() => navigate('/carts', { state: { activeApp: 'carts' } })}>
                {cadisAnimation ? (
                  <div className="shortcut-icon">
                    <Lottie animationData={cadisAnimation} loop={true} autoplay={true} style={{ width: 48, height: 48 }} />
                  </div>
                ) : (
                  <span className="shortcut-icon">📁</span>
                )}
                <span className="shortcut-label">Mes paniers</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default HomePage

